import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, FileText, Download, ExternalLink } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import ProgressiveImg from "@/components/ProgressiveImg";

/**
 * Media type integer codes (aligned with PropertyPage.jsx & CMS gallery):
 *   1 = Image, 2 = Floorplan, 3 = EPC, 4 = Brochure (PDF),
 *   5 = Video, 6 = Virtual Tour (iframe URL).
 * Everything else is rendered as a plain image.
 */
const isImageLike = (t) => t === 1 || t === 2 || t === 3;
const isVideoType = (t) => t === 5;
const isTourType = (t) => t === 6;
const isBrochureType = (t) => t === 4;
const typeBadge = (t) => {
  switch (t) {
    case 2: return "FLOORPLAN";
    case 3: return "EPC";
    case 4: return "BROCHURE";
    case 5: return "VIDEO";
    case 6: return "TOUR";
    default: return null;
  }
};

/**
 * Lightweight gallery lightbox reused from any property card / thumbnail.
 * Accepts either an array of image URLs (strings) OR the CMS gallery
 * shape (`{media_url, media_type, caption}`). Handles prev/next, keyboard
 * arrows, and Escape close.
 */
export const PropertyGalleryModal = ({ open, onOpenChange, property, initialIndex = 0 }) => {
  const items = useMemo(() => {
    if (!property) return [];
    const raw = property.gallery || [];
    const list = raw
      .filter((g) => g && (typeof g === "string" || g.media_url))
      .map((g) => (typeof g === "string"
        ? { media_url: g, media_type: 1, caption: "" }
        : g));
    // Ensure the hero image sits first if not already present.
    if (property.main_image && !list.some((g) => g.media_url === property.main_image)) {
      list.unshift({ media_url: property.main_image, media_type: 1, caption: "" });
    }
    return list;
  }, [property]);

  const [idx, setIdx] = useState(initialIndex);
  useEffect(() => {
    if (open) setIdx(Math.min(Math.max(initialIndex, 0), Math.max(items.length - 1, 0)));
  }, [open, initialIndex, items.length]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => (items.length ? (i + 1) % items.length : 0));
      if (e.key === "ArrowLeft")  setIdx((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
      if (e.key === "Escape")     onOpenChange(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, items.length, onOpenChange]);

  if (!items.length) return null;
  const current = items[idx] || items[0];
  const currentType = Number(current?.media_type) || 1;
  const isVideo = isVideoType(currentType);
  const isVirtualTour = isTourType(currentType);
  const isBrochure = isBrochureType(currentType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] p-0 bg-black border-none z-[210]"
        overlayClassName="z-[200]"
        data-testid="property-gallery-modal"
      >
        <div className="relative w-full h-[75vh] flex items-center justify-center">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-colors"
            data-testid="gallery-modal-close"
            aria-label="Close gallery"
          >
            <X className="h-5 w-5 text-black" />
          </button>
          {isVirtualTour ? (
            <iframe
              src={current.media_url}
              className="w-full h-full"
              allow="fullscreen; vr; xr; accelerometer; gyroscope"
              title={current.caption || "Virtual Tour"}
              data-testid="gallery-modal-tour"
            />
          ) : isVideo ? (
            <video
              src={resolveImageUrl(current.media_url)}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain"
              data-testid="gallery-modal-video"
            />
          ) : isBrochure ? (
            // Brochures are PDFs — embed via <object> so the browser's built-in
            // PDF viewer renders it. Includes a fallback download link if the
            // browser cannot preview PDFs inline.
            <div className="w-full h-full flex flex-col items-center justify-center bg-neutral-900 text-white gap-4 p-6">
              <object
                data={resolveImageUrl(current.media_url)}
                type="application/pdf"
                className="w-full h-full bg-white"
                data-testid="gallery-modal-brochure"
              >
                <div className="flex flex-col items-center gap-4">
                  <FileText className="h-16 w-16 opacity-80" />
                  <p className="text-sm">Your browser cannot preview this PDF inline.</p>
                  <a
                    href={resolveImageUrl(current.media_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Brochure
                  </a>
                </div>
              </object>
              <a
                href={resolveImageUrl(current.media_url)}
                download
                className="inline-flex items-center gap-2 text-xs text-white/80 hover:text-white"
                data-testid="gallery-modal-brochure-download"
              >
                <Download className="h-4 w-4" /> Download PDF
              </a>
            </div>
          ) : (
            <ProgressiveImg
              src={resolveImageUrl(current.media_url)}
              alt={current.caption || `Image ${idx + 1}`}
              className="max-w-full max-h-full object-contain"
              fill={false}
              data-testid="gallery-modal-image"
            />
          )}

          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow"
                data-testid="gallery-modal-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setIdx((i) => (i + 1) % items.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow"
                data-testid="gallery-modal-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-3 py-1 rounded-full">
                {idx + 1} / {items.length}
              </div>
            </>
          )}

          {current.caption && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded max-w-[80%] text-center">
              {current.caption}
            </div>
          )}
        </div>

        {/* Thumbnail strip — click to jump to any image */}
        {items.length > 1 && (
          <div
            className="bg-black/95 border-t border-white/10 px-3 py-2 overflow-x-auto"
            data-testid="gallery-modal-thumbstrip"
          >
            <div className="flex gap-2">
              {items.map((it, i) => {
                const t = Number(it.media_type) || 1;
                const isImg = isImageLike(t);
                const badge = typeBadge(t);
                return (
                  <button
                    key={`${it.media_url}-${i}`}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`relative shrink-0 h-14 w-20 overflow-hidden border-2 transition ${
                      i === idx ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                    data-testid={`gallery-modal-thumb-${i}`}
                    aria-label={`Show media ${i + 1}`}
                  >
                    {isImg ? (
                      <ProgressiveImg
                        src={resolveImageUrl(it.media_url)}
                        alt=""
                        loading="lazy"
                        className="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-neutral-800 text-white/80 flex items-center justify-center text-[10px] uppercase font-medium tracking-wide">
                        {badge || "MEDIA"}
                      </div>
                    )}
                    {badge && isImg && (
                      <span className="absolute bottom-0.5 left-0.5 bg-black/70 text-white text-[9px] px-1 rounded uppercase font-medium">
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PropertyGalleryModal;
